import { useState } from 'react'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { SubmitButton } from '@journ/ui/submit-button'
import { useIntlayer } from 'react-intlayer'
import type { AuthResult } from '../domain/auth'

type Props = {
	onSubmit: (email: string, password: string) => Promise<AuthResult>
}

const LoginForm = ({ onSubmit }: Props) => {
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [loading, setLoading] = useState(false)
	const content = useIntlayer('login-form')

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		setError(null)
		setLoading(true)

		try {
			const result = await onSubmit(email, password)
			if (!result.success) setError(result.error.message)
		} finally {
			setLoading(false)
		}
	}

	const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setError(null)
		setEmail(e.target.value)
	}

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setError(null)
		setPassword(e.target.value)
	}

	return (
		<form onSubmit={handleSubmit} className='flex flex-col gap-4 w-full'>
			<div className='flex flex-col gap-1.5'>
				<Label htmlFor='email' className='text-xs text-muted-foreground'>
					{content.emailLabel}
				</Label>
				<Input
					id='email'
					type='email'
					autoComplete='email'
					required
					value={email}
					onChange={handleEmailChange}
					placeholder={content.emailPlaceholder.value}
				/>
			</div>

			<div className='flex flex-col gap-1.5'>
				<Label htmlFor='password' className='text-xs text-muted-foreground'>
					{content.passwordLabel}
				</Label>
				<Input
					id='password'
					type='password'
					autoComplete='current-password'
					required
					value={password}
					onChange={handlePasswordChange}
					placeholder='••••••••'
				/>
			</div>

			{error && <p className='text-xs text-destructive'>{error}</p>}

			<SubmitButton
				type='submit'
				isSubmitting={loading}
				className='bg-primary px-6 py-4 text-secondary font-medium h-10 w-full mt-1'
			>
				{content.submit}
			</SubmitButton>
		</form>
	)
}

export { LoginForm }
